import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { watchAPI, getServerURL } from '../services/api';
import { Package, Search, Plus, Eye, EyeOff, Globe, ArrowUpDown, TrendingUp } from 'lucide-react';

const InventoryPage = () => {
  const navigate = useNavigate();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('available'); // available, sold, all
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchWatches();
  }, []);

  const fetchWatches = async () => {
    try {
      const response = await watchAPI.getAll();
      const watchData = response.data.watches || response.data || [];
      setWatches(watchData);
    } catch (error) {
      console.error('Error fetching watches:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMargin = (cost, price) => {
    const c = Number(cost) || 0;
    const p = Number(price) || 0;
    if (!c || !p) return '0.0';
    return (((p - c) / p) * 100).toFixed(1);
  };

  const calculateProfit = (watch) => {
    const cost = parseFloat(watch.cost) || 0;
    // For sold watches, use sale_price if available, otherwise asking_price
    const price = watch.status === 'Sold' && watch.sale_price
      ? parseFloat(watch.sale_price)
      : parseFloat(watch.asking_price) || 0;

    if (!cost || !price) return 0;
    return price - cost;
  };

  const calculateDaysInInventory = (createdAt) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const today = new Date();
    const diffTime = Math.abs(today - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getWatchImage = (watch) => {
    if (watch.images) {
      let images = watch.images;
      // Handle JSON string
      if (typeof images === 'string') {
        try {
          images = JSON.parse(images);
        } catch (e) {
          // If not valid JSON, might be single filename
          if (images.length > 5) {
            return `${getServerURL()}/uploads/watches/${images}`;
          }
          return null;
        }
      }
      // Handle array
      if (Array.isArray(images) && images.length > 0) {
        const firstImage = images[0];
        // Check if it's already a full URL (Cloudinary)
        if (firstImage.startsWith('http')) {
          return firstImage;
        }
        // Local file path
        return `${getServerURL()}/uploads/watches/${firstImage}`;
      }
    }
    return null;
  };

  const getImageCount = (watch) => {
    if (!watch.images) return 0;

    if (Array.isArray(watch.images)) {
      return watch.images.length;
    }

    if (typeof watch.images === 'string') {
      try {
        const parsed = JSON.parse(watch.images);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        return watch.images.length > 5 ? 1 : 0;
      }
    }

    return 0;
  };

  // Memoized stats calculations
  const stats = useMemo(() => {
    const availableWatches = watches.filter(w => w.status === 'Available');
    const soldWatches = watches.filter(w => w.status === 'Sold');

    const totalValue = availableWatches.reduce((sum, watch) => {
      return sum + (parseFloat(watch.asking_price) || 0);
    }, 0);

    const potentialProfit = availableWatches.reduce((sum, watch) => {
      return sum + calculateProfit(watch);
    }, 0);

    const actualProfit = soldWatches.reduce((sum, watch) => {
      return sum + calculateProfit(watch);
    }, 0);

    return {
      availableCount: availableWatches.length,
      soldCount: soldWatches.length,
      totalCount: watches.length,
      totalValue,
      potentialProfit,
      actualProfit
    };
  }, [watches]);

  const filteredWatches = watches
    .filter(watch => {
      // Tab filter
      if (activeTab === 'available' && watch.status !== 'Available') {
        return false;
      }
      if (activeTab === 'sold' && watch.status !== 'Sold') {
        return false;
      }
      // 'all' tab shows everything

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          watch.brand?.toLowerCase().includes(query) ||
          watch.model?.toLowerCase().includes(query) ||
          watch.reference?.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'price-high':
          return parseFloat(b.asking_price) - parseFloat(a.asking_price);
        case 'price-low':
          return parseFloat(a.asking_price) - parseFloat(b.asking_price);
        case 'margin-high':
          return parseFloat(calculateMargin(b.cost, b.asking_price)) - parseFloat(calculateMargin(a.cost, a.asking_price));
        case 'profit-high':
          return calculateProfit(b) - calculateProfit(a);
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="page-container space-y-6">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Manage your watch collection</p>
        </div>
        <button onClick={() => navigate('/inventory/add')} className="btn-primary">
          <Plus className="w-5 h-5" />
          Add Watch
        </button>
      </div>

      <div className="card">
        <div className="flex border-b border-slate-200">
          {[
            { id: 'available', label: 'Available', count: stats.availableCount, icon: Package },
            { id: 'sold', label: 'Sold', count: stats.soldCount, icon: TrendingUp },
            { id: 'all', label: 'All', count: stats.totalCount, icon: null },
          ].map(({ id, label, count, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 px-4 py-3.5 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                activeTab === id
                  ? 'text-slate-900 border-b-2 border-amber-500 bg-amber-50/50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {label} ({count})
            </button>
          ))}
        </div>

        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search brand, model, reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field py-2.5 max-w-[180px]"
            >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-high">Price: High to Low</option>
                <option value="price-low">Price: Low to High</option>
                <option value="margin-high">Margin: High to Low</option>
                <option value="profit-high">Profit: High to Low</option>
              </select>
            </div>
          </div>

        <div className="p-6 space-y-6">
          {watches.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalCount}</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-xs font-medium text-slate-600">Available</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.availableCount}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-xs font-medium text-slate-600">Value</p>
                <p className="text-2xl font-bold text-slate-900">${(Number(stats.totalValue) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="p-4 bg-emerald-50/80 rounded-xl border border-emerald-100 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="text-xs font-medium text-slate-600">Potential profit</p>
                  <p className="text-xl font-bold text-emerald-600">${(Number(stats.potentialProfit) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
                </div>
              </div>
            </div>
          )}

          {filteredWatches.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-14 h-14 mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-600 mb-1">No watches found</h3>
              <p className="text-slate-500 text-sm">
                {searchQuery ? 'Try a different search' : `No ${activeTab === 'sold' ? 'sold' : activeTab === 'available' ? 'available' : ''} watches yet`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredWatches.map((watch) => (
                <div
                  key={watch.id}
                  className="card card-body cursor-pointer hover:shadow-card-hover transition-shadow"
                  onClick={() => navigate(`/inventory/${watch.id}`)}
                >
                  <div className="relative w-full h-40 bg-slate-100 rounded-xl flex items-center justify-center mb-4 overflow-hidden">
                    <span className="text-6xl">⌚</span>
                    {getWatchImage(watch) && (
                      <img
                        src={getWatchImage(watch)}
                        alt={`${watch.brand} ${watch.model}`}
                        className="absolute inset-0 w-full h-full object-contain p-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {/* Image Count Badge */}
                    {getImageCount(watch) > 1 && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-bold">
                        📸 {getImageCount(watch)}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{watch.brand}</h3>
                  <p className="text-lg text-gray-600">{watch.model}</p>
                  {watch.reference && (
                    <p className="text-sm text-gray-400 mt-1">Ref: {watch.reference}</p>
                  )}
                </div>

                {(watch.year || watch.condition) && (
                  <div className="flex items-center gap-2 text-sm">
                    {watch.year && (
                      <span className="px-2 py-1 bg-gray-100 rounded-lg text-gray-700">
                        {watch.year}
                      </span>
                    )}
                    {watch.condition && (
                      <span className="px-2 py-1 bg-blue-100 rounded-lg text-blue-700">
                        {watch.condition}
                      </span>
                    )}
                  </div>
                )}

                <div className="pt-3 border-t border-gray-100 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">
                        {watch.status === 'Sold' ? 'Sale Price' : 'Asking Price'}
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        ${parseFloat(watch.asking_price || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {watch.status === 'Sold' ? 'Actual Profit' : 'Potential Profit'}
                      </p>
                      <p className="text-lg font-bold text-emerald-600">
                        ${calculateProfit(watch).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Margin: </span>
                      <span className="font-semibold text-green-600">
                        {calculateMargin(watch.cost || 0, watch.asking_price || 0)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Days: </span>
                      <span className="font-semibold text-gray-700">
                        {calculateDaysInInventory(watch.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      watch.status === 'Available'
                        ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                        : watch.status === 'Reserved'
                        ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700'
                        : 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-700'
                    }`}>
                      {watch.status}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/inventory/${watch.id}`);
                      }}
                      className="flex items-center gap-1 text-slate-900 hover:text-slate-900 font-semibold"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                  </div>

                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      watch.is_public
                        ? 'bg-gradient-to-r from-slate-50 to-stone-50 border-2 border-slate-200 text-slate-700'
                        : 'bg-gray-50 border-2 border-gray-200 text-gray-600'
                    }`}
                  >
                    {watch.is_public ? (
                      <>
                        <Globe className="w-4 h-4" />
                        <span>Public - Visible to other dealers</span>
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4" />
                        <span>Private - Only visible to you</span>
                      </>
                    )}
                  </div>
                </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;
